import { useMemo, useState, useEffect } from 'react'
import { FileText, RefreshCw, CheckCircle2, Clock } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useValidationStages,
  useLatestCompletedAnalysis,
  useStartValidationAnalysis,
  usePollingValidationAnalysis,
} from '@/hooks/useValidationAnalysis'
import { guidanceKeys } from '@/hooks/useGuidance'
import { useProjectStore } from '@/stores/projectStore'
import { debugLog } from '@/lib/debugLogger'
import type { ValidationStageKey, RecommendationPriority } from '@ispot/shared'
import { ValidationRadar } from './ValidationRadar'
import { ValidationRecommendations } from './ValidationRecommendations'
import { ValidationScoreBand, classifyBand } from './ValidationScoreBand'
import { ValidationStageRow } from './ValidationStageRow'
import { ValidationStageDetail } from './ValidationStageDetail'
import { EmptyState, RunningState, LoadingState } from './ValidationStates'

interface StageInfo {
  key: ValidationStageKey
  name: string
  shortName: string
  question: string
}

const STAGES: StageInfo[] = [
  { key: 'pe', name: 'Problem existence', shortName: 'Problem', question: 'Is there a real problem worth solving?' },
  { key: 'pu', name: 'Problem urgency', shortName: 'Urgency', question: 'Are customers actively seeking solutions?' },
  { key: 'psf', name: 'Problem–solution fit', shortName: 'Solution', question: 'Does your solution address the problem?' },
  { key: 'bmf', name: 'Business model fit', shortName: 'Business', question: 'Can you build a sustainable business?' },
  { key: 'pmf', name: 'Product–market fit', shortName: 'Market', question: 'Do customers love your product?' },
]

interface ValidationDashboardProps {
  onViewReport?: (artifactId: string) => void
}

export function ValidationDashboard({ onViewReport }: ValidationDashboardProps) {
  const [selectedStage, setSelectedStage] = useState<ValidationStageKey>('pe')
  const [runningAnalysisId, setRunningAnalysisId] = useState<string | null>(null)

  const { data: latestAnalysis, isLoading } = useLatestCompletedAnalysis()
  const { data: catalogStages } = useValidationStages()
  const startAnalysis = useStartValidationAnalysis()
  const { isRunning, isComplete, progress } = usePollingValidationAnalysis(runningAnalysisId)
  const projectName = useProjectStore((s) => s.currentProject?.name)
  const currentProjectId = useProjectStore((s) => s.currentProject?.id)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (isComplete && runningAnalysisId) {
      setRunningAnalysisId(null)
      if (currentProjectId) {
        queryClient.invalidateQueries({ queryKey: guidanceKeys.items(currentProjectId) })
      }
    }
  }, [isComplete, runningAnalysisId, currentProjectId, queryClient])

  const scores = useMemo(() => {
    return STAGES.reduce(
      (acc, stage) => {
        const s = latestAnalysis?.stageScores?.find((x) => x.stageKey === stage.key)
        acc[stage.key] = s?.confidenceScore ?? 0
        return acc
      },
      {} as Record<ValidationStageKey, number>,
    )
  }, [latestAnalysis])

  const overall = useMemo(() => {
    const values = STAGES.map((s) => scores[s.key] ?? 0)
    if (values.length === 0) return 0
    const sum = values.reduce((a, b) => a + b, 0)
    return Math.round(sum / values.length)
  }, [scores])

  const { strongest, weakest } = useMemo(() => {
    const sorted = [...STAGES].sort((a, b) => (scores[b.key] ?? 0) - (scores[a.key] ?? 0))
    return { strongest: sorted[0], weakest: sorted[sorted.length - 1] }
  }, [scores])

  const recommendations = useMemo(() => {
    const priorityOrder: Record<RecommendationPriority, number> = { high: 0, medium: 1, low: 2 }
    return (latestAnalysis?.stageScores ?? [])
      .flatMap((s) =>
        (s.recommendations ?? []).map((r) => ({
          ...r,
          stageName: STAGES.find((st) => st.key === s.stageKey)?.shortName ?? s.stageName,
        })),
      )
      .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
      .slice(0, 5)
  }, [latestAnalysis])

  const handleRunAnalysis = async () => {
    try {
      const analysis = await startAnalysis.mutateAsync()
      setRunningAnalysisId(analysis.id)
    } catch (error) {
      debugLog('ValidationDashboard', 'Failed to start analysis', error)
    }
  }

  if (isLoading) return <LoadingState />
  if (isRunning) return <RunningState progress={progress} />
  if (!latestAnalysis) return <EmptyState onRunAnalysis={handleRunAnalysis} />

  const selectedStageScore = latestAnalysis.stageScores?.find((s) => s.stageKey === selectedStage)
  const selectedStageInfo = STAGES.find((s) => s.key === selectedStage)
  const selectedStageCatalog = catalogStages?.find((s) => s.key === selectedStage)
  const band = classifyBand(overall)
  const radarTone = { stroke: band.color, fill: band.fillColor }
  const lastAnalyzed = latestAnalysis.completedAt
    ? new Date(latestAnalysis.completedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null

  return (
    <div
      className="h-full flex flex-col bg-bg overflow-y-auto"
      style={{ fontFamily: 'var(--font-body)' }}
    >
      <article style={{ padding: '28px clamp(24px, 5vw, 56px) 56px' }}>
        <header
          className="grid items-start"
          style={{
            gridTemplateColumns: 'minmax(0, 1fr) 480px',
            gap: 'clamp(24px, 4vw, 48px)',
            paddingBottom: 22,
          }}
        >
          <div>
            <div
              className="text-fg-muted font-mono uppercase mb-2.5"
              style={{ fontSize: 10.5, letterSpacing: '0.12em' }}
            >
              Validation analysis
              {lastAnalyzed ? ` · ${lastAnalyzed}` : ''}
            </div>
            <h1
              className="m-0 text-fg"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 28,
                lineHeight: 1.15,
                letterSpacing: '-0.015em',
                fontWeight: 600,
              }}
            >
              {projectName ? `${projectName} sits ` : 'Sits '}
              in the <em className="not-italic font-semibold" style={{ color: band.color }}>{band.label}</em> band
            </h1>
            <p
              className="text-fg-secondary"
              style={{
                margin: '12px 0 0',
                fontSize: 14,
                lineHeight: 1.55,
                maxWidth: '62ch',
              }}
            >
              Strongest on <strong className="text-fg font-semibold">{strongest.name}</strong> at{' '}
              {scores[strongest.key]}%. Weakest on{' '}
              <strong className="text-fg font-semibold">{weakest.name}</strong> at{' '}
              {scores[weakest.key]}%. Overall {overall}% across {STAGES.length} stages.
            </p>

            <div
              className="flex flex-wrap text-fg-muted"
              style={{ gap: 16, marginTop: 14, fontSize: 12.5 }}
            >
              <span className="inline-flex items-center gap-1.5">
                <FileText className="w-3 h-3" />
                {latestAnalysis.evidenceCount} evidence pieces
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3" />
                {latestAnalysis.validatedStages} of {STAGES.length} validated
              </span>
              {lastAnalyzed && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {lastAnalyzed}
                </span>
              )}
            </div>

            <div className="flex gap-2" style={{ marginTop: 16 }}>
              {latestAnalysis.artifactId && (
                <button
                  type="button"
                  onClick={() => onViewReport?.(latestAnalysis.artifactId!)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-bg-card text-fg transition-colors hover:bg-bg-secondary"
                  style={{
                    padding: '6px 12px',
                    fontSize: 12.5,
                    fontWeight: 500,
                    border: '1px solid var(--color-border-strong)',
                  }}
                >
                  <FileText className="w-3 h-3" />
                  View report
                </button>
              )}
              <button
                type="button"
                onClick={handleRunAnalysis}
                disabled={startAnalysis.isPending}
                className="inline-flex items-center gap-1.5 rounded-md text-fg-inverse transition-colors hover:bg-[#14242e] disabled:opacity-50"
                style={{
                  background: 'var(--color-fg)',
                  border: '1px solid var(--color-fg)',
                  padding: '6px 12px',
                  fontSize: 12.5,
                  fontWeight: 500,
                }}
              >
                <RefreshCw
                  className={`w-3 h-3 ${startAnalysis.isPending ? 'animate-spin' : ''}`}
                />
                Re-analyze
              </button>
            </div>
          </div>

          <ValidationRadar
            stages={STAGES}
            scores={scores}
            selectedStage={selectedStage}
            onSelectStage={setSelectedStage}
            tone={radarTone}
          />
        </header>

        <ValidationScoreBand score={overall} />

        <hr
          className="border-0 border-t border-border"
          style={{ margin: '24px 0 0' }}
        />

        <section style={{ marginTop: 22 }}>
          <div
            className="grid border-b border-border text-fg-muted font-mono uppercase"
            style={{
              gridTemplateColumns: '24px 1fr 64px 120px',
              gap: 18,
              padding: '0 0 8px',
              fontSize: 10,
              letterSpacing: '0.08em',
            }}
          >
            <span />
            <span>Stage</span>
            <span style={{ textAlign: 'right' }}>Confidence</span>
            <span style={{ textAlign: 'right' }}>Signals</span>
          </div>

          {STAGES.map((stage, i) => {
            const isSelected = stage.key === selectedStage
            const stageScore = latestAnalysis.stageScores?.find((s) => s.stageKey === stage.key)
            const catalog = catalogStages?.find((s) => s.key === stage.key)
            const totalSignalsFromCatalog = catalog?.signals?.length ?? 0
            return (
              <div key={stage.key}>
                <ValidationStageRow
                  index={i + 1}
                  stageKey={stage.key}
                  name={stage.name}
                  question={stage.question}
                  stageScore={stageScore}
                  totalSignalsFromCatalog={totalSignalsFromCatalog}
                  isSelected={isSelected}
                  onClick={() => setSelectedStage(stage.key)}
                />
                {isSelected && selectedStageInfo && (
                  <ValidationStageDetail
                    question={selectedStageInfo.question}
                    stageScore={selectedStageScore}
                    signalsFromCatalog={selectedStageCatalog?.signals ?? []}
                  />
                )}
              </div>
            )
          })}
        </section>

        <ValidationRecommendations items={recommendations} />
      </article>
    </div>
  )
}

export default ValidationDashboard
